/**
 * A phone field that looks like a US phone number and stores E.164.
 *
 * The caller holds E.164 (`+15125550142`) — the shape every `phone_e164` column
 * and Twilio itself expects — while the person sees and types `(512) 555-0142`.
 * Keeping the conversion in here is the point: every screen that rolled its own
 * produced a different string for the same number.
 *
 * `onChange` reports null until the number is complete, so a caller can save it
 * straight through without re-validating. Partial input is NOT reported as a
 * value: half a phone number is not a phone number.
 */

import { useEffect, useState, type CSSProperties } from "react";

import { formatPhoneInput, formatPhoneUS, toE164US } from "./phone";

export function PhoneInput({
  value,
  onChange,
  className = "rp-input",
  style,
  placeholder = "(512) 555-0142",
  id,
  disabled,
  readOnly,
}: {
  /** E.164, or null/"" when unset. */
  value: string | null | undefined;
  /** E.164 when complete, null while incomplete or cleared. */
  onChange: (e164: string | null) => void;
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  // Local text so the field can hold a half-typed number the parent has no
  // value for — driving it straight off `value` would erase digits as they are
  // typed, because an incomplete number normalises to null.
  const [text, setText] = useState(() => formatPhoneUS(value));

  // Follow the parent when IT changes the number (a profile load, a reset) but
  // not while the user is mid-entry: re-formatting `value` on every render is
  // what makes a caret jump to the end of the box.
  useEffect(() => {
    const incoming = formatPhoneUS(value);
    setText((current) =>
      toE164US(current) === (value || null) ? current : incoming,
    );
  }, [value]);

  return (
    <input
      id={id}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      className={className}
      style={style}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      value={text}
      onChange={(e) => {
        const next = formatPhoneInput(e.target.value);
        setText(next);
        onChange(toE164US(next));
      }}
    />
  );
}
